import { CANALIZADORES_DEL_CORPUS } from './canalizadores';
import { IDIOMAS_LISTOS } from './idiomas_listos';
import { CORPUS_ANALIZADO, CORPUS_LEGIBLES } from './corpus_cifras';
import { ORDEN_ENTIDADES } from './rayos';
import { CODIGOS_33 } from './ensenanzas33';
import { ALL_LANGUAGES, AVAILABLE_LOCALES, EUROPEAN_LANGUAGES, LATAM_LANGUAGES } from './sites';

/**
 * HASTA DÓNDE LLEGA — los números de la Def 37, hechos visibles.
 *
 * La Def 37 es el objetivo permanente y no negociable del proyecto:
 * «convertirse en la plataforma de canalización espiritual más grande del
 * mundo, LLEGANDO A LA MAYOR CANTIDAD DE PERSONAS POSIBLE», y fija sus
 * métricas: entidades cubiertas · canalizadores · canalizaciones · idiomas.
 *
 * Faina pidió enseñarlas en el sitio, y no es un adorno: es el objetivo del
 * proyecto rindiéndose cuentas a sí mismo. Un número que no sube se ve.
 *
 * 🔴 LAS TRES REGLAS DE ESTE ARCHIVO, Y NO SE NEGOCIAN:
 *
 *   1. NINGÚN NÚMERO INVENTADO, ni redondeado hacia arriba, ni «estimado».
 *      Este proyecto no puede permitirse ni una sola cosa falsa: un contador
 *      inflado es lo que más rápido destruye la confianza de una fundación, y
 *      además contradiría la regla de oro del método.
 *   2. LO QUE NO SE PUEDE MEDIR, NO SE ENSEÑA. Mejor cuatro verdaderos que
 *      seis con dos inventados. Por eso «personas» y «países» no están aquí:
 *      esperan a que existan las cuentas y la analítica.
 *   3. SE CUENTA SOLO, del propio contenido. Nada escrito a mano que pueda
 *      quedarse viejo sin que nadie se entere.
 */

/**
 * EL UNIVERSO DE CANALIZACIONES CO-CREADAS.
 *
 * 🔴 SON DOS CUERPOS, Y LOS DOS SON CO-CREADOS (Faina, 1-sep: «esas 119 las
 * co-creé contigo, pero no están en este proyecto; hay que hablar del universo
 * completo como co-creadas»).
 *
 *   · 278 — el plan del tablero: seis lentes, títulos decididos, con su vara y
 *           su anti-solape. 102 escritas, 176 pendientes.
 *   · 117 — canalizaciones propias co-creadas antes, fuera de ese plan, que
 *           estaban solo en el corpus y se publicaron el 1-sep (eran 119; 2
 *           se retiraron por solape el 1-sep: 0017CH y 0033LW).
 *
 * El universo es la suma: no son «las del proyecto» y «otras», son todas suyas
 * y todas co-creadas. Lo que cambia entre unas y otras es el momento y el grado
 * de elaboración, no la autoría.
 *
 * Se anota a mano porque el tablero se edita a mano y no se regenera nunca.
 */
const UNIVERSO_DEL_TABLERO = 278;
const PROPIAS_FUERA_DEL_TABLERO = 117;   // eran 119; 2 retiradas por solape el 1-sep (0017CH, 0033LW)
const UNIVERSO = UNIVERSO_DEL_TABLERO + PROPIAS_FUERA_DEL_TABLERO;   // 395
/* 🔢 CONTADO, no escrito (Faina 20-sep): lo escribe corpus_cl.py --escribir. */
const CORPUS = CORPUS_ANALIZADO;

/* 🔢 EL LOTE (Def 81, 11-sep · Faina 20-sep): lo que el sitio promete y mide.
   La casilla de la portada y las viñetas de los planes hablan de ESTE numero,
   no del universo del tablero (395), que es plan de trabajo y no oferta.
   🔴 297, NO 300 (Faina, 30-sep: «cambia todo lo que dice 300 a 297»). Es el
   numero de canalizaciones NUESTRAS que el sitio promete y mide.
   ⚠️ NO confundir con los lotes de 300 del corpus de TERCEROS (Def 81, Plan 4,
   `pruebas.js` §«Todo lote cerrado tiene 300 exactas»): esos siguen en 300 y
   son otra cosa. Tampoco con el precio de 300 USD del plan Colaborador. */
export const LOTE = 297;
const ENSENANZAS_33 = CODIGOS_33.length;

/* ⭐ LOS CANALIZADORES SALEN DE LA TABLA, no de un numero escrito aqui
   (Faina, 28-ago: «todas las estadisticas de las tablas que muestras en la web
   deben cuadrar con esto»). La hoja «Canalizadores» de la TABLA DE TRADUCCIONES
   es la fuente, y config/canalizadores.js se genera de ella con
   herramientas/nombres.py.

   ⚠️ ERA 251 Y SON 249. El 251 se contaba sobre las grafias del corpus, y dos
   canalizadores estaban escritos de dos maneras —«Jahn J. Kassl» / «Jahn J
   Kassl» y «Vania Rodrigues» / «Vania Rodrígues»—, asi que salian dos veces.
   El corpus sigue teniendo 5.778 cuerpos y 251 grafias; las personas son 249.

   La cifra INCLUYE a Faina (es una estadistica del corpus estudiado); la lista
   de Agradecimientos no lo incluye, y por eso alli se leen 248. */

/* (El corpus son ademas 4.297.356 palabras —contadas sumando el campo `words`
   de cuerpos_cl.jsonl el 26-ago-2026—, pero Faina retiro esa cifra de la pagina
   de la Fundacion: cuatro millones no le dicen nada a nadie. Queda aqui anotada
   por si vuelve a hacer falta.) */

/**
 * COBERTURA: cuánta gente del mundo puede leer esto en una lengua suya.
 *
 * Calculado en herramientas/cobertura.py, que compara cuatro métodos. El primero
 * dio 57 % y Faina dijo que él la tenía «sobre el 75 %». Tenía razón: ese cálculo
 * contaba solo la LENGUA MATERNA y dejaba fuera a quien lee en uno de los 33 sin
 * tenerlo de lengua materna — la India que funciona en inglés, los 235 millones
 * de Indonesia que hablan indonesio sin ser su L1, media África en francés.
 *
 * El método que vale es POR PAÍS Y AJUSTADO: cada persona cuenta una sola vez
 * —la de su país, así que no hay doble conteo— y cada país entra con la fracción
 * que de verdad puede leer en alguno de los 33 (India 0,80; Congo 0,50; España 1).
 *
 *   (1) hablantes totales      > 97 %   doble conteo, inservible
 *   (2) solo lengua materna      57 %   subestima
 *   (3) por país sin ajustar   93,7 %   sobreestima: cuenta toda la India
 *   (4) por país AJUSTADO      83,3 %   <- este, redondeado A LA BAJA a 80
 *
 * ⚠️ Los factores por país son estimaciones razonadas, no un censo, y son lo que
 * más mueve el resultado: HAY QUE REVISARLOS antes de dar el número por firme.
 */
const COBERTURA = 80;

/**
 * 🔴 Y LA COBERTURA DE HOY, QUE NO ES ESA (1-sep).
 *
 * El 80 % es la cobertura CUANDO los 55 idiomas esten traducidos. Hoy el sitio
 * se ofrece en UNO —el espanol, el unico que pasa el 90 % de traduccion— y con
 * el se llega a 20 paises: el 10 % de la humanidad.
 *
 * Las dos cifras son verdad y dicen cosas distintas, asi que van las dos: la de
 * hoy delante y la meta detras. Poner sola la de la meta bajo un rotulo que
 * acredita hechos es exactamente el fallo que Faina caza desde el 28-ago.
 *
 * Se calcula con el mismo metodo y en el mismo sitio: herramientas/cobertura.py.
 */
const COBERTURA_HOY = 10;

/* ============================================================
   🔴🔴 CIFRAS PROVISIONALES — NO PUBLICAR ASI
   ============================================================
   Faina las pidio el 26-ago para VER COMO QUEDA el bloque, sabiendo que hoy
   valen cero: «pon numeros falsos porque hoy ese numero es 0».

   Son de relleno. Un numero inventado en la pagina de una fundacion —y en
   concreto un numero de suscriptores— no es un detalle de maquetacion: es lo
   que un periodista, un donante o un regulador comprobaria primero, y todo el
   proyecto se sostiene sobre no inventar nada.

   Por eso NO se escriben sueltos entre los datos verdaderos, sino aqui, con
   una marca que las pruebas leen: cada vez que se corre `node pruebas/pruebas.js`
   sale un aviso en pantalla mientras esta bandera siga en true.

   PARA PUBLICAR: poner los valores reales y bajar la bandera de aqui abajo.
   Las hectareas ya tienen su numero de verdad (30), pero la donacion sigue en
   proceso de formalizacion, asi que tampoco es publicable todavia. */
/* 🟢 20-sep (Faina: «actualiza todas las cifras»): ya no hay relleno. Los
   valores de abajo son los reales de hoy, ceros incluidos. */
export const PROVISIONALES = false;

const HECTAREAS = 30;        // real, pero la donacion no esta formalizada
const SUSCRIPTORES = 0;      // real (20-sep): el registro todavia no esta abierto
/* 🟢 YA NO ES RELLENO (Faina, 1-sep). Antes era 42, un numero puesto para ver
   como quedaba el bloque, porque «paises cubiertos» se entendia como «paises
   desde donde nos visitan» y sin visitas no hay dato.
   Ahora mide otra cosa, que SI se puede calcular: a cuantos paises llegamos por
   IDIOMA, sumando los tres sitios. Sale de herramientas/cobertura.py — 113 de
   los 120 mas poblados, el 97 % de la humanidad— y la etiqueta lo dice.
   ⚠️ Es alcance PLANIFICADO: los idiomas todavia no estan traducidos. */
/* 🟢 20-sep: la cifra de HOY, coherente con la portada (Faina: «idiomas 1/55»).
   Las canalizaciones se leen hoy en un idioma, el español, y con el se llega a
   20 de los 120 paises mas poblados (cobertura.py: 809 M, el 10 %). Los 113
   son a donde se llega con los 55 idiomas del contenido: meta, no hoy. */
const PAISES = 20;
const DONANTES = 1;          // real (Faina, 20-sep: «donantes debe decir 1»)

/* ============================================================
   CUANTOS TRABAJAMOS — lo que hay sobre lo que se busca
   ============================================================
   Faina, 1-sep: «si el numero de directores es 3/4, consejeros 1/33,
   colaboradores 2 / n.º de paises del mundo, empleados 0/0».

   ⭐ NO ES UN CONTADOR, ES UNA DECLARACION. Cada casilla dice a la vez donde
   estamos y a donde vamos, y por eso el cero de los empleados tambien se
   ensena: «0/0» no es un hueco por llenar, es una decision — no queremos
   plantilla. Es la misma honestidad de «todavia no hay ninguna hectarea».

   EL DENOMINADOR DE CADA UNO, y de donde sale:
     · directores    4  — los tres actuales mas el cuarto que entra al cumplir
                          18 anos (esta previsto en la gobernanza).
     · consejeros   33  — el numero de la Fundacion: la edad de Jeshua, los 33
                          grandes mensajes. Aqui es una meta declarada.
     · empleados     0  — a proposito. Ver arriba.
     · colaboradores 195 — los paises del mundo: 193 estados miembros de la ONU
                          mas los 2 observadores. Uno por pais es la meta.

   🔴 LOS NUMERADORES SON DATO, NO ADORNO. Los de directores y empleados
   constan; los de consejeros y colaboradores los dio Faina el 1-sep. Si alguno
   cambia, se cambia AQUI y la pagina se entera sola. */
/** Los idiomas de la Fundacion: la union de los tres sitios (Def 77). */
export const IDIOMAS_DE_LOS_TRES = [
  ...new Set([...ALL_LANGUAGES, ...EUROPEAN_LANGUAGES, ...LATAM_LANGUAGES])
];

export const EQUIPO = [
  { clave: 'directores',    hay: 3, meta: 4 },
  { clave: 'consejeros',    hay: 1, meta: 33 },
  { clave: 'empleados',     hay: 0, meta: 0 },
  { clave: 'colaboradores', hay: 2, meta: 195 }
];

export const ALCANCE = {
  /** Publicadas de verdad, contadas del propio contenido. */
  universo: UNIVERSO,

  /** Idiomas: los que tienen traducción, de los 33 del objetivo (Def 35). */
  idiomas: AVAILABLE_LOCALES.length,
  /* 🔴 LOS DE LOS TRES SITIOS, NO LOS DEL .ong (Def 77, 1-sep). Los 33 de
     jeshua.es NO son los mismos que los de jeshua.ong: solo comparten once, y
     los otros 22 son lenguas europeas. Contar solo ALL_LANGUAGES decia 33
     cuando la Fundacion publica en 55. */
  idiomasObjetivo: IDIOMAS_DE_LOS_TRES.length,

  /** Voces del sistema, incluidas las que aún no tienen pieza publicada. */
  voces: ORDEN_ENTIDADES.length,

  /** El corpus que hay detrás: es lo que hace exactas a las de arriba. */
  corpus: CORPUS,
  /* 🔴 Y LO QUE DE VERDAD SE PUEDE LEER, que no es lo mismo (1-sep). El corpus
     reunido son 5.778 cuerpos —esa es la cifra que describe el estudio—, pero
     de ahi salen las ya publicadas como nuestras y las 107 rotas en origen. El
     plan 4 promete un numero de canalizaciones: tiene que prometer ESTE, que es
     el que entrega. Se cuenta en corpus_cl.py, no se escribe a mano. */
  corpusLegibles: CORPUS_LEGIBLES,
  canalizadores: CANALIZADORES_DEL_CORPUS,

  /** 🔴 Provisionales: ver PROVISIONALES arriba. */
  hectareas: HECTAREAS,
  suscriptores: SUSCRIPTORES,
  paises: PAISES,
  donantes: DONANTES,
  cobertura: COBERTURA
};

/**
 * LO QUE ESTA EN EL TABLERO Y AUN NO ESCRITO, por voz.
 *
 * Se anota a mano porque el TABLERO se edita a mano y no se regenera nunca. Y
 * existe para que una voz sin piezas publicadas NO SE ESCONDA: se ensena
 * diciendo cuantas vienen, porque ocultarla seria hacer creer que no existe.
 */
export const EN_EL_TABLERO = {
  'Los Esenios': 9
};

/** Con separador de miles, que 5778 se lee peor que 5.778. */
export const conPunto = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '.');

/**
 * Los cuatro cuadros (Faina, 26-ago): el universo entero, los idiomas, las
 * voces y la cobertura. Sin «de»: los tres primeros dicen lo que el proyecto
 * ES —278 canalizaciones, 33 idiomas, 14 voces—, no lo que lleva hecho.
 */
/**
 * 🟥 EL CUADRO DE LA PORTADA — LO QUE HAY SOBRE LO QUE SE BUSCA (1-sep).
 *
 * Estaba mal, y era un fallo de los que la regla dura persigue: bajo el rotulo
 * «Nuestro contenido» —que acredita HECHOS— habia tres cifras de META.
 *
 *     decia                        habia
 *     397 canalizaciones           221 publicadas
 *     55 idiomas                   1, el espanol
 *     80 % cobertura               10 %, la de ese idioma
 *     14 voces                     14  ✅ la unica verdadera
 *
 * El candado no lo veia: vigila las cifras de «Donde estamos hoy» y estas se le
 * escapaban. Y el 397 lo puse yo el mismo dia que Faina corrigio el universo.
 *
 * LA SOLUCION NO ES BORRAR LA AMBICION, y no hizo falta inventarla: Faina ya
 * habia definido el formato para esto mismo el 1-sep, en «Cuantos trabajamos»
 * —3/4 directores, 1/33 consejeros—. Aqui se usa el suyo. El numero de la
 * izquierda es lo que se puede contar hoy en el propio contenido; el de la
 * derecha, tenue, es a donde vamos.
 *
 * Las voces llevan su meta igual que las demas —14/14— aunque ya este cumplida
 * (Faina, 1-sep). Es lo correcto: las cuatro casillas dicen lo mismo de la
 * misma manera, y la unica que ha llegado se ve porque los dos numeros
 * coinciden. Sin ella, la casilla parecia de otra clase.
 */
/* 🟥 LAS CIFRAS DEL 20-SEP (Faina): «Canalizaciones 253/300 (hoy /297) · Idiomas 1/55 ·
   Voces 14/14 · Enseñanzas 33/33 · cobertura ?/80».
   · La meta de canalizaciones es EL LOTE (300), no el universo del tablero.
   · IDIOMAS cuenta los del CONTENIDO —en cuantos idiomas se pueden leer las
     canalizaciones—, no los de la interfaz: el sitio se ofrece en 55, pero las
     canalizaciones estan hoy solo en español. Se cuenta del propio catalogo.
   · ENSEÑANZAS: cuantas de las 33 tienen al menos una canalizacion publicada
     que la enseña. Se cuenta del catalogo, nunca se escribe. */

/* 🟥 FUERA EL CORPUS DE «LO QUE HEMOS HECHO» (Faina, 23-sep: «no quiero hacer
   mencion a eso»). Las dos casillas que decian «5.568 canalizaciones
   analizadas» y «249 canalizadores estudiados» eran las unicas del sitio que
   se apoyaban en el trabajo de terceros, y van en la misma direccion que la
   decision de derechos: el corpus es interno y no se publica (Def 79). En su
   lugar entran dos que son NUESTRAS y se cuentan en el propio catalogo.
   ⛔ Los idiomas NO entran todavia: «vamos a salir con español», y 55 arriba
   junto a «1 idioma» abajo se leeria como una promesa, no como un hecho. */

export const buildReach = (stats) => {
  const published = stats ? stats.published : 0;
  const teachings = stats ? stats.teachings : 0;
  const contentLanguages = stats ? stats.contentLanguages : 0;
  return {
    published,
    teachings,
    contentLanguages,
    voices: stats ? stats.voices : 0,
    songs: stats ? stats.songs : 0,
    cuadros: [
      { clave: 'home.alcance_canalizaciones', hay: published, meta: LOTE, punto: true },
      { clave: 'home.alcance_idiomas', hay: contentLanguages, meta: ALCANCE.idiomasObjetivo },
      { clave: 'home.alcance_voces', hay: ALCANCE.voces, meta: ALCANCE.voces },
      { clave: 'home.alcance_ensenanzas', hay: teachings, meta: ENSENANZAS_33 },
      { clave: 'home.alcance_cobertura', hay: COBERTURA_HOY, meta: COBERTURA, sufijo: '%' }
    ]
  };
};
